import React, { useState } from "react";
import { Box, IconButton, InputBase, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { fmtPct } from "../../../utils/format";
import { parseMarginDraft } from "./derive";

interface MarginEditCellProps {
  /** Margen guardado (el que aplica desde mañana). */
  value: number;
  /** Borrador en curso; undefined = no se está editando. */
  draft: string | undefined;
  onStart: () => void;
  onDraftChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Celda editable del "Margen % (act.)". Clic → input; Enter guarda, Esc
 * cancela. Los clics dentro no llegan a la selección de celdas de la tabla.
 */
const MarginEditCell: React.FC<MarginEditCellProps> = ({
  value,
  draft,
  onStart,
  onDraftChange,
  onCancel,
  onSave,
}) => {
  const [hover, setHover] = useState(false);

  if (draft === undefined) {
    return (
      <Tooltip title="Editar el margen desde mañana" enterDelay={350}>
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "text",
          }}
        >
          <EditIcon sx={{ fontSize: 12, opacity: hover ? 1 : 0.55 }} />
          {fmtPct(value)}
        </Box>
      </Tooltip>
    );
  }

  const parsed = parseMarginDraft(draft);
  const canSave = parsed !== null && parsed !== value;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
    >
      <InputBase
        autoFocus
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canSave) onSave();
          if (e.key === "Escape") onCancel();
        }}
        inputProps={{ inputMode: "decimal", "aria-label": "Margen % (act.)" }}
        error={parsed === null}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          "& input": { textAlign: "right", py: 0, px: 0.5 },
          border: 1,
          borderColor: parsed === null ? "error.main" : "primary.main",
          borderRadius: 0.5,
        }}
      />
      <Tooltip title="Guardar">
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!canSave}
            onClick={onSave}
            sx={{ p: 0.25 }}
          >
            <CheckIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cancelar">
        <IconButton size="small" onClick={onCancel} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MarginEditCell;
